/**
 * useScenarioPusher — poll les scénarios pédagogiques pushés par le formateur
 * et les applique en jeu via `applyPushedScenario`.
 *
 * 11/02/2026 (Phase 3 — Superpouvoirs formateur).
 *
 * Cadence : identique à useSupervisorNotesPoller (mount + monthly + 10 min).
 * Silent on failure. Auto-ack après application pour éviter les re-triggers.
 */
import { useEffect, useRef } from 'react';
import useGameStore from '../store/gameStore';
import useLicenseStore from '../store/licenseStore';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_INTERVAL_MS = 10 * 60 * 1000;

async function fetchPendingScenarios(licenseKey) {
  if (!licenseKey || !BACKEND) return [];
  try {
    const resp = await fetch(
      `${BACKEND}/api/master/scenarios/pending?license_key=${encodeURIComponent(licenseKey)}`,
    );
    if (!resp.ok) return [];
    const data = await resp.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch (_) {
    return [];
  }
}

async function ackScenario(scenarioId, licenseKey) {
  if (!scenarioId || !licenseKey || !BACKEND) return;
  try {
    await fetch(
      `${BACKEND}/api/master/scenarios/${encodeURIComponent(scenarioId)}/ack?license_key=${encodeURIComponent(licenseKey)}`,
      { method: 'POST' },
    );
  } catch (_) { /* silent */ }
}

async function drainScenarios(licenseKey) {
  const state = useGameStore.getState();
  if (!state.gameStarted || typeof state.applyPushedScenario !== 'function') return;
  const scenarios = await fetchPendingScenarios(licenseKey);
  if (!scenarios.length) return;
  for (const sc of scenarios) {
    const res = state.applyPushedScenario(sc);
    // Ack quel que soit le résultat (already_applied = déjà appliqué, on
    // ack quand même pour purger).
    if (res && (res.applied || res.reason === 'already_applied')) {
      await ackScenario(sc.id, licenseKey);
    }
  }
}

export default function useScenarioPusher() {
  const licenseKey = useLicenseStore((s) => s.licenseKey);
  const monthRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!licenseKey) return undefined;
    const initial = useGameStore.getState();
    if (initial.gameStarted && !startedRef.current) {
      startedRef.current = true;
      monthRef.current = `${initial.year}-${initial.month}`;
      drainScenarios(licenseKey);
    }
    const unsubscribe = useGameStore.subscribe((state) => {
      const tag = `${state.year}-${state.month}`;
      if (state.gameStarted && !startedRef.current) {
        startedRef.current = true;
        monthRef.current = tag;
        drainScenarios(licenseKey);
        return;
      }
      if (state.gameStarted && monthRef.current && tag !== monthRef.current) {
        monthRef.current = tag;
        drainScenarios(licenseKey);
      }
    });
    return unsubscribe;
  }, [licenseKey]);

  useEffect(() => {
    if (!licenseKey) return undefined;
    const id = setInterval(() => {
      const state = useGameStore.getState();
      if (state.gameStarted) drainScenarios(licenseKey);
    }, FALLBACK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [licenseKey]);
}
